import { useRef, useState } from "react";
import { GripVertical, ImagePlus, Loader2, Star, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { filesService, resolveUploadUrl, UploadFolder } from "@/services/filesService";
import { getApiErrorMessage } from "@/services/httpClient";
import { cn } from "@/lib/utils";
import { IMAGE_SPECS } from "@/lib/imageStandards";
import { processImageForUpload } from "@/lib/processImageForUpload";

export interface MultiImageUploadProps {
  label?: string;
  value: string[];
  onChange: (urls: string[]) => void;
  folder: UploadFolder;
  maxImages?: number;
  disabled?: boolean;
  className?: string;
}

const productSpec = IMAGE_SPECS.product;

const MultiImageUpload = ({
  label = "Images",
  value,
  onChange,
  folder,
  maxImages = 6,
  disabled,
  className,
}: MultiImageUploadProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFiles = async (files: FileList | null) => {
    if (!files?.length || disabled) return;
    const remaining = maxImages - value.length;
    if (remaining <= 0) return;

    const batch = Array.from(files).slice(0, remaining);
    setError(null);
    setUploading(true);
    try {
      const processed = await Promise.all(batch.map((f) => processImageForUpload(f, productSpec)));
      const results =
        processed.length === 1
          ? [await filesService.upload(processed[0], folder)]
          : await filesService.uploadMany(processed, folder);
      onChange([...value, ...results.map((r) => r.fileUrl)]);
    } catch (err) {
      setError(getApiErrorMessage(err, "Upload failed"));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeAt = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const setPrimary = (index: number) => {
    if (index === 0) return;
    const next = [...value];
    const [item] = next.splice(index, 1);
    next.unshift(item);
    onChange(next);
  };

  return (
    <div className={cn("space-y-2", className)}>
      {label && <Label>{label}</Label>}
      <div className="flex flex-wrap gap-3">
        {value.map((url, index) => (
          <div key={`${url}-${index}`} className="relative group">
            <div className="h-28 w-28 overflow-hidden rounded-xl border border-border bg-white">
              <img
                src={resolveUploadUrl(url)}
                alt=""
                className="h-full w-full object-contain"
              />
            </div>
            {index === 0 && (
              <span className="absolute left-1 top-1 rounded bg-primary px-1.5 py-0.5 text-[10px] font-medium text-primary-foreground">
                Primary
              </span>
            )}
            {!disabled && (
              <div className="absolute inset-0 flex items-center justify-center gap-1 rounded-xl bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                {index !== 0 && (
                  <Button type="button" size="icon" variant="secondary" className="h-7 w-7" onClick={() => setPrimary(index)}>
                    <Star className="h-3.5 w-3.5" />
                  </Button>
                )}
                <Button type="button" size="icon" variant="destructive" className="h-7 w-7" onClick={() => removeAt(index)}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </div>
        ))}
        {value.length < maxImages && (
          <button
            type="button"
            disabled={disabled || uploading}
            onClick={() => inputRef.current?.click()}
            className="flex h-28 w-28 flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-border bg-muted/30 text-muted-foreground hover:bg-muted/50 disabled:opacity-50"
          >
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
            <span className="text-[10px]">Add</span>
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        className="hidden"
        disabled={disabled || uploading}
        onChange={(e) => handleFiles(e.target.files)}
      />
      <p className="text-xs text-muted-foreground flex items-center gap-1">
        <GripVertical className="h-3 w-3" />
        {productSpec.label} square · first image is primary · up to {maxImages} images
      </p>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
};

export default MultiImageUpload;
